import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search,
  Download,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

export default function TransactionsPage() {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
    hasMore: true,
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const response = await api.get(`/transactions/?${params.toString()}`);
      setTransactions(response.data);
      setPagination(prev => ({
        ...prev,
        hasMore: response.data.length === pagination.limit
      }));
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filters.status, filters.startDate, filters.endDate, pagination.offset]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: { variant: 'default', className: 'bg-green-500/20 text-green-500 border-green-500/30', icon: CheckCircle2 },
      failed: { variant: 'destructive', className: 'bg-red-500/20 text-red-500 border-red-500/30', icon: XCircle },
      pending: { variant: 'secondary', className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', icon: Clock },
      refunded: { variant: 'outline', className: 'bg-purple-500/20 text-purple-500 border-purple-500/30', icon: RotateCcw },
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`${config.className} rounded-sm`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.toUpperCase()}
      </Badge>
    );
  };

  const handleRefund = async (txnId) => {
    try {
      await api.post(`/transactions/${txnId}/refund`, { reason: 'Customer request' });
      toast.success('Refund initiated successfully');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate refund');
    }
  };

  const exportToCSV = () => {
    const headers = ['Transaction ID', 'Amount', 'Status', 'Customer Email', 'MDR', 'Net Amount', 'Date'];
    const rows = transactions.map(txn => [
      txn.id,
      txn.amount,
      txn.status,
      txn.customer_email,
      txn.mdr_amount,
      txn.net_amount,
      txn.created_at
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export downloaded');
  };

  const filteredTransactions = transactions.filter(txn => 
    filters.search === '' || 
    txn.id.toLowerCase().includes(filters.search.toLowerCase()) ||
    txn.customer_email.toLowerCase().includes(filters.search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Transactions</h1>
          <p className="text-muted-foreground text-sm">View and manage your payment transactions</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchTransactions}
            className="rounded-sm"
            data-testid="refresh-txns-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            size="sm" 
            onClick={exportToCSV}
            className="rounded-sm"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-grid">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by ID or email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9 rounded-sm"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className="w-[150px] rounded-sm" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-[150px] rounded-sm"
              data-testid="start-date-filter"
            />
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-[150px] rounded-sm"
              data-testid="end-date-filter"
            />
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card className="card-grid">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="table-high-density">
              <TableHeader>
                <TableRow className="border-b border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Transaction ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">MDR</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Net Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(8)].map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredTransactions.length > 0 ? (
                  filteredTransactions.map((txn) => (
                    <TableRow key={txn.id} className="border-b border-border/50" data-testid={`txn-row-${txn.id}`}>
                      <TableCell className="font-mono text-sm">{txn.id.slice(0, 12)}...</TableCell>
                      <TableCell className="font-mono tabular-nums">{formatCurrency(txn.amount)}</TableCell>
                      <TableCell>{getStatusBadge(txn.status)}</TableCell>
                      <TableCell className="text-sm">{txn.customer_email}</TableCell>
                      <TableCell className="font-mono tabular-nums text-muted-foreground">{formatCurrency(txn.mdr_amount)}</TableCell>
                      <TableCell className="font-mono tabular-nums">{formatCurrency(txn.net_amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(txn.created_at)}</TableCell>
                      <TableCell>
                        {txn.status === 'success' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRefund(txn.id)}
                            className="h-7 text-xs"
                            data-testid={`refund-btn-${txn.id}`}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Refund
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No transactions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {pagination.offset + 1} to {pagination.offset + filteredTransactions.length} transactions
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.offset === 0}
            onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
            className="rounded-sm"
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.hasMore}
            onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
            className="rounded-sm"
            data-testid="next-page-btn"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
